import React, { useState, useMemo, useLayoutEffect } from 'react';
import moment, { Moment } from 'moment-timezone';
import { useHistory } from 'react-router-dom';
import {
  CallRequest,
  downloadCallCalendar,
  useRequestCancelMutation,
  useActiveCalls,
  ActiveCall,
  useCallRequestStatus,
} from '../../../hooks/api/useCalls';
import { Avatar } from '../../avatar';
import { useTagGroups } from '../../../hooks/api/useTagGroups';
import useAuth from '../../../hooks/utils/useAuth';
import { CalendarIcon, PhoneWhite26 } from '../../../assets/images/svgs';
import { SchedulingModal } from '../scheduling-modal/scheduling-modal';
import { CancelCallModal } from '../cancel-call-modal/cancel-call-modal';
import { HowCallWorksModal } from '../how-call-works-modal';
import { Availability } from '../../../hooks/api/useCalls';
import Toast from '../../common/pop-up-message';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTE_PATH } from '../../../routes/route-paths';
import { DrawerModal } from '../drawer-modal';
import { usePeerDetailLite } from '../../../hooks/api/usePeers';
import { useCurrentDomain } from '../../../hooks/api/useDomains';

export interface HowCallWorksContext {
  selectedCallId?: number;
  selectedActiveCallId?: number;
}

interface CallDetailsModalProps {
  open: boolean;
  onClose: () => void;
  callRequests: CallRequest[];
  initialSelectedCallId?: number;
  /** When reopening after "How a call works", pass the active call id to restore the in-progress call view. */
  initialSelectedActiveCallId?: number;
  initialCallRequestData?: any; // CreateCallRequestResponse with _isNowCall, _listenerName, _listenerId
  /** When provided, "How a call works" will close this modal and open How Call Works in the parent; parent should reopen call details with the context when How Call Works closes. */
  onOpenHowCallWorks?: (context: HowCallWorksContext) => void;
}

// Close icon component
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={`text-label-gray ${className ?? ''}`}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Phone number formatting function to display in (XXX) XXX-XXXX format
const formatPhoneNumberDisplay = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  // Remove leading +1 if present
  const cleaned = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
  // Format as (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  // Return original if not 10 digits
  return phoneNumber;
};

// Get raw phone number for tel: links (digits only, with +1 prefix if needed)
const getRawPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  // If it's 10 digits, add +1 prefix for tel: link
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  // If it already has country code, add + prefix if missing
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  // Return with + prefix if it doesn't have one
  return digits.startsWith('+') ? digits : `+${digits}`;
};

// Header component (without border since drawer header has it)
const CallDetailsHeader: React.FC<{ onClose: () => void; title: string; showPhoneIcon?: boolean }> = ({
  onClose,
  title,
  showPhoneIcon = false,
}) => (
  <div className="flex items-center gap-2">
    {showPhoneIcon ? (
      <div className="relative h-8 w-8 shrink-0">
        <div className="bg-success absolute inset-0 rounded-full border-2 border-white" />
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={PhoneWhite26} alt="Phone" className="h-4 w-4" />
        </div>
      </div>
    ) : (
      <img
        src={CalendarIcon}
        alt="Calendar"
        className="h-6 w-6 shrink-0"
        style={{ filter: 'brightness(0) saturate(100%)' }}
      />
    )}
    <p className="font-albert-sans text-label-gray flex-1 text-xl font-medium leading-7">{title}</p>
    <button
      onClick={onClose}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-50"
      data-testid="call-details-close-button"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
);

// Pending call details view - Content Only
const PendingCallDetailsContent: React.FC<{
  callRequest: CallRequest;
  onCancelCall: (requestId: number) => Promise<void>;
  onOpenHowCallWorks: () => void;
  isCancelling?: boolean;
  isPassed?: boolean;
}> = ({ callRequest, onCancelCall, onOpenHowCallWorks, isCancelling, isPassed }) => {
  const history = useHistory();
  const connection = callRequest.connections[0];
  const listener = connection.listener_role;
  const callIsPassed = isPassed ?? connection.status === 'passed';

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col items-center gap-6">
        {/* Introductory Text */}
        <div className="w-full">
          <p className="font-albert-sans text-base font-medium leading-6 text-black">
            {callIsPassed
              ? `${listener.user.display_name} is unfortunately unavailable. Let's help you look for another listener.`
              : `Finalizing your call schedule.`}
          </p>
        </div>

        {/* Call Details Card */}
        <div className="bg-button-secondary flex w-full max-w-[646px] flex-col items-center gap-3.5 rounded-[12px] px-6 py-6 md:h-[240px] md:flex-row md:items-center md:py-0">
          {/* Listener Info (Left Side) */}
          <div className="flex w-[194px] shrink-0 flex-col items-start gap-2">
            <div className="flex h-[150px] w-full justify-center overflow-hidden">
              <div className="h-[150px] w-[150px] overflow-hidden rounded-full border border-slate-300">
                {listener.profile_photo_square_file_url ? (
                  <img
                    src={listener.profile_photo_square_file_url}
                    alt={listener.user.display_name || 'Avatar'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gray-200">
                    {listener.user.display_name && (
                      <span className="text-2xl font-semibold text-gray-600">
                        {listener.user.display_name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex w-full flex-col items-center justify-center">
              <p className="text-primary font-albert-sans w-full text-center text-base font-semibold leading-6">
                {listener.user.display_name}
              </p>
            </div>
          </div>

          {/* Call Status and Actions (Right Side) */}
          <div className="flex flex-1 flex-col items-center justify-center gap-3">
            <p className="font-albert-sans px-4 py-2 text-center text-xl font-semibold leading-none text-black">
              {callIsPassed
                ? `Your Listener isn't available at this time`
                : `We are syncing with your Listener's availability.`}
            </p>
            {!callIsPassed && (
              <p className="text-primary font-albert-sans text-base font-medium leading-7">
                <button
                  onClick={() => onCancelCall(callRequest.id)}
                  disabled={isCancelling}
                  className="hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel Request'}
                </button>
              </p>
            )}
            {callIsPassed && (
              <button
                onClick={() => {
                  history.push(ROUTE_PATH.BROWSE_PEER_LISTENERS);
                }}
                className="bg-button-primary mt-4 flex w-full items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:mt-8 md:w-auto"
              >
                Browse Peer Listeners
              </button>
            )}
          </div>
        </div>

        {/* What happens on a call link */}
        <div className="flex w-full justify-center">
          <button
            onClick={onOpenHowCallWorks}
            className="text-primary font-albert-sans text-center text-base font-medium leading-6 underline hover:no-underline"
          >
            What happens on a call?
          </button>
        </div>
      </div>
    </div>
  );
};

// Footer for Pending Call Details
const PendingCallDetailsFooter: React.FC<{
  onClose: () => void;
  onBack?: () => void;
}> = ({ onClose, onBack }) => (
  <div className="flex flex-row flex-wrap items-center justify-end gap-3 md:gap-4">
    {onBack && (
      <button
        onClick={onBack}
        className="text-primary bg-button-secondary flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:min-w-0 md:flex-none"
      >
        Back
      </button>
    )}
    <button
      onClick={onClose}
      className="bg-button-primary flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:min-w-0 md:flex-none"
    >
      Close
    </button>
  </div>
);

// Single call details view - Content Only
const SingleCallDetailsContent: React.FC<{
  callRequest: CallRequest;
  onAddToCalendar: (requestId: number) => Promise<void>;
  onCancelCall: (requestId: number) => Promise<void>;
  onRescheduleCall: () => void;
  onOpenHowCallWorks: () => void;
  isCancelling?: boolean;
  isDownloadingCalendar?: boolean;
}> = ({
  callRequest,
  onAddToCalendar,
  onCancelCall,
  onRescheduleCall,
  onOpenHowCallWorks,
  isCancelling,
  isDownloadingCalendar,
}) => {
  const { user } = useAuth();
  const { data: topicTagGroups } = useTagGroups();
  const userTimeZone = user?.timezone ? user.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;

  const connection = callRequest.connections[0];
  const listener = connection.listener_role;
  const scheduledAt = connection.scheduled_at ? moment.utc(connection.scheduled_at).tz(userTimeZone) : null;

  const formatCallTime = () => {
    if (!scheduledAt) return { time: 'Now', date: '' };
    const timeStr = scheduledAt.format('h:mma').toLowerCase();
    const dateStr = scheduledAt.format('dddd, MMMM D');
    return { time: timeStr, date: dateStr };
  };

  const { time: callTime, date: callDate } = formatCallTime();

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col items-center gap-6">
        {/* Introductory Text */}
        <div className="w-full">
          <p className="font-albert-sans text-base font-medium leading-6 text-black">
            You have an upcoming call scheduled with {listener.user.display_name}.
          </p>
        </div>

        {/* Call Details Card */}
        <div className="bg-button-secondary flex w-full max-w-[646px] flex-col items-center gap-3.5 rounded-[12px] px-6 py-6 md:h-[240px] md:flex-row md:py-0">
          {/* Listener Info (Left Side) */}
          <div className="flex w-[194px] shrink-0 flex-col items-start gap-2">
            <div className="flex h-[150px] w-full justify-center overflow-hidden">
              <div className="h-[150px] w-[150px] overflow-hidden rounded-full border border-slate-300">
                {listener.profile_photo_square_file_url ? (
                  <img
                    src={listener.profile_photo_square_file_url}
                    alt={listener.user.display_name || 'Avatar'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gray-200">
                    {listener.user.display_name && (
                      <span className="text-2xl font-semibold text-gray-600">
                        {listener.user.display_name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex w-full flex-col items-center justify-center">
              <p className="text-primary font-albert-sans w-full text-center text-base font-semibold leading-6">
                {listener.user.display_name}
              </p>
            </div>
          </div>

          {/* Call Time and Actions (Right Side) */}
          <div className="flex flex-1 flex-col items-center gap-3 md:items-start">
            <p className="font-albert-sans whitespace-nowrap text-xl font-semibold leading-none text-black md:text-3xl">
              Call Starting @ {callTime}
            </p>
            <p className="font-albert-sans h-[34px] w-full text-center text-base font-semibold leading-none text-black md:text-left md:text-2xl">
              {callDate}
            </p>
            <p className="text-primary font-albert-sans flex w-full items-center justify-center gap-1 text-base font-medium leading-7 md:block">
              <button
                onClick={() => onAddToCalendar(callRequest.id)}
                disabled={isDownloadingCalendar}
                className="text-xs hover:underline disabled:cursor-not-allowed disabled:opacity-50 md:text-base"
              >
                {isDownloadingCalendar ? 'Downloading...' : 'Add to Calendar'}
              </button>
              <span className="text-xs md:text-base">{'  |  '}</span>
              <button
                onClick={() => onCancelCall(callRequest.id)}
                disabled={isCancelling}
                className="text-xs hover:underline disabled:cursor-not-allowed disabled:opacity-50 md:text-base"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Call'}
              </button>
              <span className="text-xs md:text-base">{'  |  '}</span>
              <button onClick={onRescheduleCall} className="text-xs hover:underline md:text-base">
                Reschedule Call
              </button>
            </p>
          </div>
        </div>

        {/* What happens on a call link */}
        <div className="flex w-full justify-center">
          <button
            onClick={onOpenHowCallWorks}
            className="text-primary font-albert-sans text-center text-base font-medium leading-6 underline hover:no-underline"
          >
            What happens on a call?
          </button>
        </div>
      </div>
    </div>
  );
};

// Footer for Single Call Details
const SingleCallDetailsFooter: React.FC<{
  onClose: () => void;
  onBack?: () => void;
}> = ({ onClose, onBack }) => (
  <div className="flex flex-row flex-wrap items-center justify-end gap-3 md:gap-4">
    {onBack && (
      <button
        onClick={onBack}
        className="text-primary bg-button-secondary flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:min-w-0 md:flex-none"
      >
        Back
      </button>
    )}
    <button
      onClick={onClose}
      className="bg-button-primary flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:min-w-0 md:flex-none"
    >
      Close
    </button>
  </div>
);

// Active call details view - Content Only
const ActiveCallDetailsContent: React.FC<{
  activeCall: ActiveCall;
  onOpenHowCallWorks: () => void;
}> = ({ activeCall, onOpenHowCallWorks }) => {
  const { data: currentDomain } = useCurrentDomain(true);
  const dialInNumber = currentDomain?.phone_number || activeCall.phone_number;
  const formattedPhoneNumber = formatPhoneNumberDisplay(dialInNumber);
  const rawPhoneNumber = getRawPhoneNumber(dialInNumber);

  // Check if the call is pending (listener hasn't accepted yet) or accepted and starting
  const connection = activeCall.request.connections?.[0];
  const isPending = connection?.status === 'offered';
  const isAccepted = connection?.status === 'accepted';

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col gap-6">
        {/* Instructions Text */}
        <div className="flex flex-col gap-1">
          {isPending ? (
            <p className="font-albert-sans text-label-gray text-base font-medium leading-6">
              We are confirming your listener's availability and we will call soon.
            </p>
          ) : isAccepted ? (
            <>
              <p className="font-albert-sans text-label-gray text-base font-medium leading-6">
                We have confirmed your listener's availability and we will call soon.
              </p>
              <p className="font-albert-sans text-label-gray text-base font-medium leading-6">&nbsp;</p>
              <p className="font-albert-sans text-label-gray text-base font-medium leading-6">
                <span>If you don't receive a call in the next 5 minutes, please dial </span>
                <a href={`tel:${rawPhoneNumber}`} className="text-button-primary hover:underline">
                  {formattedPhoneNumber}
                </a>
                <span> to connect.</span>
              </p>
            </>
          ) : (
            // Fallback to pending message if status is unclear
            <p className="font-albert-sans text-label-gray text-base font-medium leading-6">
              We are confirming your listener's availability and we will call soon.
            </p>
          )}
        </div>

        {/* How does a call work link */}
        <div className="flex flex-col items-center gap-px">
          <button
            onClick={onOpenHowCallWorks}
            className="text-primary font-albert-sans w-full whitespace-pre-wrap text-center text-sm font-medium leading-6"
          >
            How does a call work?
          </button>
          <div className="relative h-0 w-[144px]">
            <div className="border-primary absolute left-0 right-0 top-0 border-b" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer for Active Call Details
const ActiveCallDetailsFooter: React.FC<{
  onClose: () => void;
  onBack?: () => void;
}> = ({ onClose, onBack }) => (
  <div className="flex flex-row flex-wrap items-center justify-between gap-3 md:gap-4">
    {onBack && (
      <button
        onClick={onBack}
        className="text-primary bg-button-secondary flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:min-w-0 md:flex-none"
      >
        Back
      </button>
    )}
    <button
      onClick={onClose}
      className="bg-button-primary flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:min-w-0 md:flex-none"
    >
      Close
    </button>
  </div>
);

// Pending call list item component
const PendingCallListItem: React.FC<{
  callRequest: CallRequest;
  onClick: () => void;
}> = ({ callRequest, onClick }) => {
  const { data: topicTagGroups } = useTagGroups();
  const connection = callRequest.connections[0];
  const listener = connection.listener_role;

  const topicName = useMemo(() => {
    const tagGroupIds = callRequest.tag_group_ids;
    if (tagGroupIds && tagGroupIds.length > 0 && topicTagGroups) {
      const tagGroup = topicTagGroups.find((tg) => tg.id === tagGroupIds[0]);
      return tagGroup?.name || 'General';
    }
    return 'General';
  }, [callRequest.tag_group_ids, topicTagGroups]);

  return (
    <button
      onClick={onClick}
      className="border-input-border-gray flex w-full items-center gap-4 rounded-lg border bg-white p-4 text-left transition-colors hover:bg-gray-50"
    >
      <Avatar
        imageUrl={listener.profile_photo_square_file_url}
        name={listener.user.display_name}
        className="h-12 w-12 shrink-0"
      />
      <div className="flex flex-1 flex-col gap-1">
        <p className="font-albert-sans text-label-gray text-base font-medium leading-5">{listener.user.display_name}</p>
        <p className="font-albert-sans text-placeholder-gray text-sm font-normal leading-5">
          Waiting for listener to accept
        </p>
        <p className="font-albert-sans text-placeholder-gray text-sm font-normal leading-5">{topicName}</p>
      </div>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.5 15L12.5 10L7.5 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-placeholder-gray"
        />
      </svg>
    </button>
  );
};

// Call list item component for scheduled calls
const CallListItem: React.FC<{
  callRequest: CallRequest;
  onClick: () => void;
}> = ({ callRequest, onClick }) => {
  const { user } = useAuth();
  const { data: topicTagGroups } = useTagGroups();
  const userTimeZone = user?.timezone ? user.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;

  const connection = callRequest.connections[0];
  const listener = connection.listener_role;
  const scheduledAt = connection.scheduled_at ? moment.utc(connection.scheduled_at).tz(userTimeZone) : null;

  const topicName = useMemo(() => {
    const tagGroupIds = callRequest.tag_group_ids;
    if (tagGroupIds && tagGroupIds.length > 0 && topicTagGroups) {
      const tagGroup = topicTagGroups.find((tg) => tg.id === tagGroupIds[0]);
      return tagGroup?.name || 'General';
    }
    return 'General';
  }, [callRequest.tag_group_ids, topicTagGroups]);

  const formatScheduledTime = () => {
    if (!scheduledAt) return 'Now';
    const timeStr = scheduledAt.format('h:mm a');
    const dateStr = scheduledAt.format('MMM D, ddd');
    if (scheduledAt.isSame(moment().tz(userTimeZone), 'day')) {
      return `Today, ${timeStr}`;
    }
    return `${dateStr}, ${timeStr}`;
  };

  return (
    <button
      onClick={onClick}
      className="border-input-border-gray flex w-full items-center gap-4 rounded-lg border bg-white p-4 text-left transition-colors hover:bg-gray-50"
    >
      <Avatar
        imageUrl={listener.profile_photo_square_file_url}
        name={listener.user.display_name}
        className="h-12 w-12 shrink-0"
      />
      <div className="flex flex-1 flex-col gap-1">
        <p className="font-albert-sans text-label-gray text-base font-medium leading-5">{listener.user.display_name}</p>
        <p className="font-albert-sans text-placeholder-gray text-sm font-normal leading-5">{formatScheduledTime()}</p>
        <p className="font-albert-sans text-placeholder-gray text-sm font-normal leading-5">{topicName}</p>
      </div>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.5 15L12.5 10L7.5 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-placeholder-gray"
        />
      </svg>
    </button>
  );
};

// Active call list item component
const ActiveCallListItem: React.FC<{
  activeCall: ActiveCall;
  onClick: () => void;
}> = ({ activeCall, onClick }) => {
  const { data: topicTagGroups } = useTagGroups();
  const listener = activeCall.listener_role;

  const topicName = useMemo(() => {
    const tagGroupIds = activeCall.request.tag_group_ids;
    if (tagGroupIds && tagGroupIds.length > 0 && topicTagGroups) {
      const tagGroup = topicTagGroups.find((tg) => tg.id === tagGroupIds[0]);
      return tagGroup?.name || 'General';
    }
    return 'General';
  }, [activeCall.request.tag_group_ids, topicTagGroups]);

  return (
    <button
      onClick={onClick}
      className="border-input-border-gray flex w-full items-center gap-4 rounded-lg border bg-white p-4 text-left transition-colors hover:bg-gray-50"
    >
      <div className="relative h-12 w-12 shrink-0">
        <Avatar
          imageUrl={listener.profile_photo_square_file_url}
          name={listener.user.display_name}
          className="h-12 w-12"
        />
        <div className="bg-success absolute -right-4 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white">
          <img src={PhoneWhite26} alt="Phone" className="h-3 w-3" />
        </div>
      </div>
      <div className="ml-2 flex flex-1 flex-col gap-1">
        <p className="font-albert-sans text-label-gray text-base font-medium leading-5">{listener.user.display_name}</p>
        <p className="font-albert-sans text-success text-sm font-normal leading-5">Call in progress</p>
        <p className="font-albert-sans text-placeholder-gray text-sm font-normal leading-5">{topicName}</p>
      </div>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.5 15L12.5 10L7.5 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-placeholder-gray"
        />
      </svg>
    </button>
  );
};

// List view component - Content Only
const CallListContent: React.FC<{
  callRequests: CallRequest[];
  pendingRequests: CallRequest[];
  activeCalls: ActiveCall[];
  onSelectCall: (callRequest: CallRequest) => void;
  onSelectActiveCall: (activeCall: ActiveCall) => void;
}> = ({ callRequests, pendingRequests, activeCalls, onSelectCall, onSelectActiveCall }) => {
  const hasItems = activeCalls.length > 0 || callRequests.length > 0 || pendingRequests.length > 0;

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col gap-3">
        {hasItems ? (
          <>
            {/* Active calls first */}
            {activeCalls.map((activeCall) => (
              <ActiveCallListItem
                key={`active-${activeCall.id}`}
                activeCall={activeCall}
                onClick={() => onSelectActiveCall(activeCall)}
              />
            ))}
            {/* Pending requests */}
            {pendingRequests.map((callRequest) => (
              <PendingCallListItem
                key={callRequest.id}
                callRequest={callRequest}
                onClick={() => onSelectCall(callRequest)}
              />
            ))}
            {/* Scheduled calls */}
            {callRequests.map((callRequest) => (
              <CallListItem key={callRequest.id} callRequest={callRequest} onClick={() => onSelectCall(callRequest)} />
            ))}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="font-albert-sans text-placeholder-gray text-base font-medium leading-6">
              No upcoming calls scheduled
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Footer for Call List View
const CallListFooter: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => (
  <div className="flex flex-row flex-wrap items-center justify-end gap-3 md:gap-4">
    <button
      onClick={onClose}
      className="bg-button-primary flex min-w-[120px] flex-1 items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:min-w-0 md:flex-none"
    >
      Close
    </button>
  </div>
);

export const CallDetailsModal: React.FC<CallDetailsModalProps> = ({
  open,
  onClose,
  callRequests,
  initialSelectedCallId,
  initialSelectedActiveCallId,
  initialCallRequestData,
  onOpenHowCallWorks: onOpenHowCallWorksFromParent,
}) => {
  const [selectedCall, setSelectedCall] = useState<CallRequest | null>(null);
  const [selectedActiveCall, setSelectedActiveCall] = useState<ActiveCall | null>(null);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState<{
    listenerId: number;
    availabilities: Availability[];
    listenerName: string;
    listenerImage?: string;
    originalRequestId: number;
    initialTime?: Moment;
    initialTagGroupId?: number;
    initialText?: string;
  } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDownloadingCalendar, setIsDownloadingCalendar] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [callToCancel, setCallToCancel] = useState<{ requestId: number; listenerName: string } | null>(null);
  const [isHowCallWorksModalOpen, setIsHowCallWorksModalOpen] = useState(false);
  const cancelMutation = useRequestCancelMutation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: activeCalls } = useActiveCalls(user?.caller_role_id);

  // Fetch listener details when rescheduling if availabilities are missing
  const { data: peerDetailLite } = usePeerDetailLite(rescheduleData?.listenerId);

  // Construct temporary CallRequest from initialCallRequestData if it's a "now call"
  const temporaryPendingRequest = useMemo(() => {
    if (!initialCallRequestData || !initialCallRequestData._isNowCall) return null;

    const data = initialCallRequestData;
    const now = new Date().toISOString();

    // Construct minimal CallRequest from CreateCallRequestResponse
    const tempRequest: CallRequest = {
      id: data.id,
      call_id: data.call_id,
      caller_role_id: data.caller_role_id,
      audio_file_url: data.audio_file_url,
      audio_duration: data.audio_duration || 0,
      call_units_length: 0,
      connections_processed_at: data.connections_processed_at?.toString() || now,
      created_at: data.created_at,
      updated_at: data.updated_at,
      is_backup_request: data.is_backup_request,
      original_backup_request_id: data.original_backup_request_id,
      original_request_id: data.original_request_id,
      package_id: data.package_id,
      partner_code: data.partner_code || '',
      perform_backup_requests: data.perform_backup_requests,
      status: data.status as 'new' | 'responded' | 'backup_pending' | 'backup_found',
      text: data.text || '',
      tag_group_ids: data.topic?.tag_id ? [data.topic.tag_id] : [],
      topic: data.topic as any,
      topic_id: data.topic_id,
      connections: [
        {
          id: 0, // Temporary ID
          request_id: data.id,
          listener_role_id: data._listenerId,
          listener_role: {
            id: data._listenerId,
            user: {
              id: 0,
              display_name: data._listenerName,
              first_name: data._listenerName.split(' ')[0] || data._listenerName,
            },
            profile_photo_file_url: data._listenerImage || '',
            profile_photo_square_file_url: data._listenerImage || '',
            about_me: '',
            availabilities: [],
            available: false,
            available_now: false,
            background_traits: [],
            can_take_calls: false,
            connection_blocked: false,
            favorite_id: 0,
            is_available: false,
            is_active: false,
            is_peer_active: false,
            listener_audio: [],
            military_tag: '',
            next_available_text: '',
            peer_status: '',
            primary_language_tag: '',
            profile_traits: [],
            secondary_language_tag: '',
            share_link_url: '',
            spirituality_tag: '',
            state: '',
            status: '',
            tier_id: 0,
            type: '',
          } as any,
          status: 'matched',
          scheduled_at: null,
          created_at: now,
          updated_at: now,
          offered_at: now,
          evaluation_ends_at: now,
          expired: false,
          listener_acknowledged: false,
          package_id: data.package_id,
          tier_id: 0,
        },
      ],
    };

    return tempRequest;
  }, [initialCallRequestData]);

  // Filter pending requests (matched status or backup_pending, not scheduled)
  // Include temporary pending request if it exists and isn't already in callRequests
  // Optimistically remove requests that have become active calls
  const pendingRequests = useMemo(() => {
    // Helper to check if a request has become an active call
    const hasActiveCall = (request: CallRequest): boolean => {
      if (!activeCalls || activeCalls.length === 0) return false;
      return activeCalls.some(
        (activeCall) => (request.call_id && activeCall.id === request.call_id) || activeCall.request.id === request.id,
      );
    };

    const requests: CallRequest[] = [];

    // Add temporary request if it exists and matches initialSelectedCallId
    if (temporaryPendingRequest && temporaryPendingRequest.id === initialSelectedCallId) {
      // Check if it's already in callRequests or has become active
      const existsInCallRequests = callRequests?.some((cr) => cr.id === temporaryPendingRequest.id);
      if (!existsInCallRequests && !hasActiveCall(temporaryPendingRequest)) {
        requests.push(temporaryPendingRequest);
      }
    }

    // Add requests from callRequests
    if (callRequests) {
      const filtered = callRequests.filter(
        (request) =>
          request.connections?.length > 0 &&
          (request.connections[0]?.status === 'matched' || request.status === 'backup_pending') &&
          request.connections[0]?.scheduled_at === null &&
          !request.is_backup_request &&
          !hasActiveCall(request), // Optimistically exclude requests that have become active
      );
      requests.push(...filtered);
    }

    // Sort by created_at, most recent first
    return requests.sort((a, b) => {
      const aTime = a.connections?.[0]?.created_at || a.created_at;
      const bTime = b.connections?.[0]?.created_at || b.created_at;
      if (!aTime) return 1;
      if (!bTime) return -1;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  }, [callRequests, temporaryPendingRequest, initialSelectedCallId, activeCalls]);

  // Filter and sort scheduled calls
  const scheduledCalls = useMemo(() => {
    if (!callRequests) return [];
    return callRequests
      .filter(
        (request) =>
          request.connections?.length > 0 &&
          (request.connections[0]?.status === 'accepted' || request.connections[0]?.status === 'offered'),
      )
      .filter((request) => request.connections[0]?.scheduled_at !== null)
      .filter((request) => !request.is_backup_request)
      .sort((a, b) => {
        const aTime = a.connections?.[0]?.scheduled_at;
        const bTime = b.connections?.[0]?.scheduled_at;
        if (!aTime) return 1;
        if (!bTime) return -1;
        return new Date(aTime).getTime() - new Date(bTime).getTime();
      });
  }, [callRequests]);

  // Determine which pending request ID to poll for status changes
  // Either the selected pending call OR the only pending request if shown directly
  const pendingRequestIdToWatch = useMemo(() => {
    if (!open) return undefined;

    // If we have a selected call that's a pending request, watch it
    if (selectedCall) {
      const connection = selectedCall.connections[0];
      if ((connection?.status === 'matched' || connection?.status === 'passed') && connection?.scheduled_at === null) {
        return selectedCall.id;
      }
    }

    // If there's only one pending request and no active/scheduled calls, it will be shown directly
    const hasOnlyOnePending =
      pendingRequests.length === 1 && (!activeCalls || activeCalls.length === 0) && scheduledCalls.length === 0;
    if (hasOnlyOnePending && !selectedCall) {
      return pendingRequests[0].id;
    }

    return undefined;
  }, [open, selectedCall, pendingRequests, activeCalls, scheduledCalls.length]);

  // Poll the specific pending request to detect status changes (e.g., passed)
  const { data: polledRequestStatus } = useCallRequestStatus(pendingRequestIdToWatch, {
    enabled: !!pendingRequestIdToWatch,
    refetchInterval: 3000, // Poll every 3 seconds
  });

  // Helper to check if a specific request ID is passed (checks polled data first)
  const isRequestPassed = React.useCallback(
    (requestId: number, fallbackStatus?: string) => {
      // First check the polled data (most up-to-date)
      if (polledRequestStatus && polledRequestStatus.id === requestId) {
        return polledRequestStatus.connections[0]?.status === 'passed';
      }
      // Fall back to the provided status
      return fallbackStatus === 'passed';
    },
    [polledRequestStatus],
  );

  // Optimistically remove passed calls from the list
  React.useEffect(() => {
    if (!polledRequestStatus) return;

    const status = polledRequestStatus.connections[0]?.status;
    if (status === 'passed') {
      // Optimistically update the cache to remove the passed call
      queryClient.setQueryData<CallRequest[]>(['callRequests'], (old) => {
        if (!old) return old;
        return old.filter((cr) => cr.id !== polledRequestStatus.id);
      });
    }
  }, [polledRequestStatus, queryClient]);

  // When reopening with initialSelectedActiveCallId, restore the in-progress (active) call view.
  // useLayoutEffect so selection is applied before paint and avoids a flash of list view.
  useLayoutEffect(() => {
    if (open && initialSelectedActiveCallId && activeCalls?.length) {
      const activeCallToSelect = activeCalls.find((ac) => ac.id === initialSelectedActiveCallId);
      if (activeCallToSelect) {
        setSelectedActiveCall(activeCallToSelect);
        setSelectedCall(null);
      }
    }
  }, [open, initialSelectedActiveCallId, activeCalls]);

  // Set selected call when modal opens with initialSelectedCallId (skip if restoring active call view).
  // useLayoutEffect for immediate selection so the first paint shows the right view.
  useLayoutEffect(() => {
    if (open && initialSelectedCallId && !initialSelectedActiveCallId) {
      // Check temporary pending request first (for "now calls")
      if (temporaryPendingRequest && temporaryPendingRequest.id === initialSelectedCallId) {
        setSelectedCall(temporaryPendingRequest);
        return;
      }

      // Check scheduled calls
      const callToSelect = scheduledCalls.find((call) => call.id === initialSelectedCallId);
      if (callToSelect) {
        setSelectedCall(callToSelect);
        return;
      }

      // Check pending requests
      const pendingCallToSelect = pendingRequests.find((call) => call.id === initialSelectedCallId);
      if (pendingCallToSelect) {
        setSelectedCall(pendingCallToSelect);
        return;
      }

      // If call not found yet, try again after a short delay (query might still be refetching)
      if (initialSelectedCallId) {
        let retryCount = 0;
        const maxRetries = 5;
        const retryInterval = 500; // 500ms between retries

        const retryFindCall = () => {
          retryCount++;

          // Check temporary pending request first
          if (temporaryPendingRequest && temporaryPendingRequest.id === initialSelectedCallId) {
            setSelectedCall(temporaryPendingRequest);
            return;
          }

          const retryScheduledCall = scheduledCalls.find((call) => call.id === initialSelectedCallId);
          if (retryScheduledCall) {
            setSelectedCall(retryScheduledCall);
            return;
          }

          const retryPendingCall = pendingRequests.find((call) => call.id === initialSelectedCallId);
          if (retryPendingCall) {
            setSelectedCall(retryPendingCall);
            return;
          }

          // Continue retrying if not found and haven't exceeded max retries
          if (retryCount < maxRetries) {
            setTimeout(retryFindCall, retryInterval);
          }
        };

        const timeoutId = setTimeout(retryFindCall, retryInterval);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    open,
    initialSelectedCallId,
    initialSelectedActiveCallId,
    scheduledCalls,
    pendingRequests,
    temporaryPendingRequest,
  ]);

  // Prioritize showing active call when modal opens (only if no initialSelectedCallId and only one item total)
  React.useEffect(() => {
    const totalItems = (activeCalls?.length || 0) + scheduledCalls.length + pendingRequests.length;
    if (
      open &&
      !initialSelectedCallId &&
      activeCalls &&
      activeCalls.length > 0 &&
      !selectedActiveCall &&
      !selectedCall &&
      totalItems === 1
    ) {
      // If there's exactly one active call and nothing else, show it automatically
      setSelectedActiveCall(activeCalls[0]);
    }
  }, [
    open,
    initialSelectedCallId,
    activeCalls,
    selectedActiveCall,
    selectedCall,
    scheduledCalls.length,
    pendingRequests.length,
  ]);

  // Automatically switch to active call view when a "now call" is accepted
  React.useEffect(() => {
    if (!open || !activeCalls || activeCalls.length === 0) return;

    // Check if we're displaying a pending "now call" that has become active
    const currentPendingRequest = selectedCall || (pendingRequests.length === 1 ? pendingRequests[0] : null);
    if (!currentPendingRequest) return;

    // Check if this pending request now has an active call
    const matchingActiveCall = activeCalls.find(
      (activeCall) =>
        (currentPendingRequest.call_id && activeCall.id === currentPendingRequest.call_id) ||
        activeCall.request.id === currentPendingRequest.id,
    );

    if (matchingActiveCall) {
      // Switch to showing the active call
      setSelectedActiveCall(matchingActiveCall);
      setSelectedCall(null);
    }
  }, [open, activeCalls, selectedCall, pendingRequests]);

  // Update selectedCall when callRequests data changes (e.g., when a call is passed)
  React.useEffect(() => {
    if (selectedCall && callRequests) {
      const updatedCall = callRequests.find((cr) => cr.id === selectedCall.id);
      if (updatedCall) {
        // Update selectedCall with the latest data, especially if status changed
        setSelectedCall(updatedCall);
      }
    }
  }, [callRequests, selectedCall?.id]);

  // Reset selected call when modal closes
  React.useEffect(() => {
    if (!open) {
      setSelectedCall(null);
      setSelectedActiveCall(null);
      setIsRescheduleModalOpen(false);
      setRescheduleData(null);
      setIsCancelModalOpen(false);
      setCallToCancel(null);
      // Don't reset isHowCallWorksModalOpen here - it should persist even when call details modal closes
    }
  }, [open]);

  const handleAddToCalendar = async (requestId: number) => {
    try {
      setIsDownloadingCalendar(true);
      await downloadCallCalendar(requestId);
      Toast.success('Calendar file downloaded');
    } catch (error: any) {
      Toast.error(error.message || 'Failed to download calendar file');
    } finally {
      setIsDownloadingCalendar(false);
    }
  };

  const handleCancelCallClick = (callRequest: CallRequest) => {
    const listenerName = callRequest.connections[0]?.listener_role?.user?.display_name || '';
    setCallToCancel({ requestId: callRequest.id, listenerName });
    setIsCancelModalOpen(true);
  };

  const handleCancelCall = async (requestId: number): Promise<void> => {
    // Check in callRequests first
    let callRequest = callRequests?.find((cr) => cr.id === requestId);

    // If not found, check temporary pending request (for "now calls" that haven't been refetched yet)
    if (!callRequest && temporaryPendingRequest && temporaryPendingRequest.id === requestId) {
      callRequest = temporaryPendingRequest;
    }

    if (callRequest) {
      handleCancelCallClick(callRequest);
    } else {
      // If still not found, try to cancel directly (for edge cases)
      try {
        setIsCancelling(true);
        await cancelMutation.mutateAsync(requestId);
        Toast.success('Call request cancelled successfully');
        queryClient.invalidateQueries(['callRequests']);
        // Clear selection if this was the selected call
        if (selectedCall?.id === requestId) {
          setSelectedCall(null);
        }
        // Clear temporary pending request if it matches
        if (temporaryPendingRequest?.id === requestId) {
          // The temporary request will be cleared when the query refetches
        }
        // Close modal if no other calls to show
        const totalItems = (activeCalls?.length || 0) + scheduledCalls.length + pendingRequests.length;
        if (totalItems <= 1) {
          onClose();
        }
      } catch (error: any) {
        Toast.error(error.response?.data?.description || 'Failed to cancel call request');
      } finally {
        setIsCancelling(false);
      }
    }
  };

  const handleCancelCallConfirm = async () => {
    if (!callToCancel) return;

    const requestIdToCancel = callToCancel.requestId;
    const listenerNameToCancel = callToCancel.listenerName;

    // Store previous data for potential rollback
    const previousCallRequests = queryClient.getQueryData<CallRequest[]>(['callRequests']);

    try {
      setIsCancelling(true);

      // Optimistically update the cache to remove the cancelled item
      queryClient.setQueryData<CallRequest[]>(['callRequests'], (old) => {
        if (!old) return old;
        return old.filter((cr) => cr.id !== requestIdToCancel);
      });

      // Clear selection if this was the selected call
      if (selectedCall?.id === requestIdToCancel) {
        setSelectedCall(null);
      }

      setIsCancelModalOpen(false);
      setCallToCancel(null);

      // Calculate remaining items after optimistic removal
      const remainingItems =
        (activeCalls?.length || 0) +
        scheduledCalls.filter((c) => c.id !== requestIdToCancel).length +
        pendingRequests.filter((c) => c.id !== requestIdToCancel).length;

      // Close modal if no other calls to show
      if (remainingItems === 0) {
        onClose();
      }

      await cancelMutation.mutateAsync(requestIdToCancel);
      Toast.success('Call request cancelled successfully');

      // Invalidate queries to ensure we have the latest data
      queryClient.invalidateQueries(['callRequests']);
    } catch (error: any) {
      // Roll back optimistic update on error
      if (previousCallRequests) {
        queryClient.setQueryData(['callRequests'], previousCallRequests);
      }
      // Restore the cancel modal state
      setCallToCancel({ requestId: requestIdToCancel, listenerName: listenerNameToCancel });
      setIsCancelModalOpen(true);
      Toast.error(error.response?.data?.description || 'Failed to cancel call request');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelModalClose = () => {
    if (!isCancelling) {
      setIsCancelModalOpen(false);
      setCallToCancel(null);
    }
  };

  // Compute final availabilities for rescheduling: use fetched ones if original are empty
  const rescheduleAvailabilities = useMemo(() => {
    if (!rescheduleData) return [];

    const finalAvailabilities =
      rescheduleData.availabilities.length > 0 ? rescheduleData.availabilities : peerDetailLite?.availabilities || [];

    return finalAvailabilities;
  }, [rescheduleData, peerDetailLite]);

  const handleRescheduleCall = (callRequest: CallRequest) => {
    const connection = callRequest.connections[0];
    const listener = connection.listener_role;
    const userTimeZone = user?.timezone ? user.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert scheduled_at from UTC to user's timezone
    let scheduledAt: Moment | undefined = undefined;
    if (connection.scheduled_at) {
      const utcMoment = moment.utc(connection.scheduled_at);
      scheduledAt = utcMoment.tz(userTimeZone);
    }

    // Get availabilities from listener, or use empty array if not available
    // We'll fetch them using usePeerDetailLite if they're missing
    const listenerAvailabilities = listener.availabilities || [];

    // Set up reschedule data with initial values from the previous call
    // Pass the original request ID so the backend can properly link the rescheduled request
    // The initialTime will be pre-selected in the scheduling modal
    const rescheduleDataToSet = {
      listenerId: connection.listener_role_id,
      availabilities: listenerAvailabilities,
      listenerName: listener.user.display_name,
      listenerImage: listener.profile_photo_square_file_url,
      originalRequestId: callRequest.id,
      initialTime: scheduledAt,
      initialTagGroupId:
        callRequest.tag_group_ids && callRequest.tag_group_ids.length > 0 ? callRequest.tag_group_ids[0] : undefined,
      initialText: callRequest.text || undefined,
    };

    setRescheduleData(rescheduleDataToSet);
    setIsRescheduleModalOpen(true);
  };

  const handleRescheduleModalClose = async (context?: 'scheduled' | 'cancelled' | null, callRequestData?: any) => {
    const originalRequestId = rescheduleData?.originalRequestId;
    setIsRescheduleModalOpen(false);
    setRescheduleData(null);

    if (context === 'scheduled' && callRequestData) {
      // Optimistically remove the rescheduled (old) call from the list so both don't show until refetch
      if (originalRequestId != null) {
        queryClient.setQueryData<CallRequest[]>(['callRequests'], (old) => {
          if (!old) return old;
          return old.filter((cr) => cr.id !== originalRequestId);
        });
      }
      // Invalidate queries to trigger refetch and get the new call in the list
      queryClient.invalidateQueries(['callRequests']);

      Toast.success('Call rescheduled successfully');

      // Check if callRequestData has the structure of a CallRequest with connections
      // If it does, we can use it directly
      if (
        callRequestData.connections &&
        Array.isArray(callRequestData.connections) &&
        callRequestData.connections.length > 0
      ) {
        setSelectedCall(callRequestData as CallRequest);
        // Keep the modal open to show the new call details
      } else {
        // Otherwise, wait for the parent to refetch and update callRequests
        // Try to find the new call request in the updated callRequests list
        const findNewCall = () => {
          const newCallRequest = callRequests?.find((cr) => cr.id === callRequestData.id);
          if (newCallRequest) {
            setSelectedCall(newCallRequest);
            // Keep the modal open to show the new call details
          } else {
            // If we can't find it, close the modal - the user can reopen it to see the new call
            onClose();
          }
        };

        // Try immediately, then again after a short delay
        findNewCall();
        setTimeout(findNewCall, 1000);
      }
    } else if (context === 'cancelled' || context === null) {
      // If user cancelled the reschedule, just close the modal
      onClose();
    }
  };

  const handleOpenHowCallWorks = () => {
    if (onOpenHowCallWorksFromParent) {
      onOpenHowCallWorksFromParent({
        selectedCallId: selectedCall?.id ?? selectedActiveCall?.request?.id,
        selectedActiveCallId: selectedActiveCall?.id,
      });
      onClose();
    } else {
      setIsHowCallWorksModalOpen(true);
    }
  };

  // Check if a call request has an active call
  const getActiveCallForRequest = (callRequest: CallRequest): ActiveCall | null => {
    if (!activeCalls) return null;
    return (
      activeCalls.find(
        (activeCall) =>
          (callRequest.call_id && activeCall.id === callRequest.call_id) || activeCall.request.id === callRequest.id,
      ) || null
    );
  };

  // Helper to check if a call request is pending
  const isPendingRequest = useMemo(
    () =>
      (callRequest: CallRequest): boolean => {
        return (
          pendingRequests.some((req) => req.id === callRequest.id) ||
          ((callRequest.connections[0]?.status === 'matched' ||
            callRequest.connections[0]?.status === 'passed' ||
            callRequest.status === 'backup_pending') &&
            callRequest.connections[0]?.scheduled_at === null &&
            !callRequest.is_backup_request)
        );
      },
    [pendingRequests],
  );

  // Calculate if there are multiple items (memoized)
  const hasMultipleItems = useMemo(
    () => (activeCalls?.length || 0) + scheduledCalls.length + pendingRequests.length > 1,
    [activeCalls?.length, scheduledCalls.length, pendingRequests.length],
  );

  // When parent controls How Call Works (onOpenHowCallWorksFromParent), return null as soon as call details is closed.
  // Otherwise, keep rendering if How Call Works is still open (so we can show it).
  if (!open) {
    if (onOpenHowCallWorksFromParent) return null;
    if (!isHowCallWorksModalOpen) return null;
  }

  // Helper to render header based on current view
  const renderHeader = () => {
    if (selectedActiveCall) {
      return (
        <CallDetailsHeader onClose={onClose} title="We are processing your call connection." showPhoneIcon={true} />
      );
    }
    if (selectedCall) {
      const activeCall = getActiveCallForRequest(selectedCall);
      if (activeCall) {
        return (
          <CallDetailsHeader onClose={onClose} title="We are processing your call connection." showPhoneIcon={true} />
        );
      }
      return <CallDetailsHeader onClose={onClose} title="Call Details" />;
    }
    return <CallDetailsHeader onClose={onClose} title="Upcoming Calls" />;
  };

  // Helper to render footer based on current view
  const renderFooter = () => {
    if (selectedActiveCall) {
      return (
        <ActiveCallDetailsFooter
          onClose={onClose}
          onBack={
            hasMultipleItems
              ? () => {
                  setSelectedActiveCall(null);
                  setSelectedCall(null);
                }
              : undefined
          }
        />
      );
    }
    if (selectedCall) {
      const activeCall = getActiveCallForRequest(selectedCall);
      const callIsPending = isPendingRequest(selectedCall);

      if (activeCall) {
        return (
          <ActiveCallDetailsFooter
            onClose={onClose}
            onBack={
              hasMultipleItems
                ? () => {
                    setSelectedCall(null);
                    setSelectedActiveCall(null);
                  }
                : undefined
            }
          />
        );
      }
      if (callIsPending) {
        return (
          <PendingCallDetailsFooter
            onClose={onClose}
            onBack={hasMultipleItems ? () => setSelectedCall(null) : undefined}
          />
        );
      }
      return (
        <SingleCallDetailsFooter
          onClose={onClose}
          onBack={hasMultipleItems ? () => setSelectedCall(null) : undefined}
        />
      );
    }
    return <CallListFooter onClose={onClose} />;
  };

  // If there's a selected active call, show active call view
  if (selectedActiveCall) {
    return (
      <>
        {open && (
          <DrawerModal open={open} onClose={onClose} header={renderHeader()} footer={renderFooter()}>
            <ActiveCallDetailsContent activeCall={selectedActiveCall} onOpenHowCallWorks={handleOpenHowCallWorks} />
          </DrawerModal>
        )}
        {!onOpenHowCallWorksFromParent && (
          <HowCallWorksModal open={isHowCallWorksModalOpen} onClose={() => setIsHowCallWorksModalOpen(false)} />
        )}
      </>
    );
  }

  // If there's a selected call, check if it's active, pending, or scheduled and show appropriate view
  if (selectedCall) {
    const activeCall = getActiveCallForRequest(selectedCall);
    const showActiveView = activeCall !== null;
    const callIsPending = isPendingRequest(selectedCall);

    return (
      <>
        {open && (
          <DrawerModal open={open} onClose={onClose} header={renderHeader()} footer={renderFooter()}>
            {showActiveView && activeCall ? (
              <ActiveCallDetailsContent activeCall={activeCall} onOpenHowCallWorks={handleOpenHowCallWorks} />
            ) : callIsPending ? (
              <PendingCallDetailsContent
                callRequest={selectedCall}
                onCancelCall={handleCancelCall}
                onOpenHowCallWorks={handleOpenHowCallWorks}
                isCancelling={isCancelling}
                isPassed={isRequestPassed(selectedCall.id, selectedCall.connections[0]?.status)}
              />
            ) : (
              <SingleCallDetailsContent
                callRequest={selectedCall}
                onAddToCalendar={handleAddToCalendar}
                onCancelCall={handleCancelCall}
                onRescheduleCall={() => handleRescheduleCall(selectedCall)}
                onOpenHowCallWorks={handleOpenHowCallWorks}
                isCancelling={isCancelling}
                isDownloadingCalendar={isDownloadingCalendar}
              />
            )}
          </DrawerModal>
        )}
        {isRescheduleModalOpen && rescheduleData && (
          <div className="fixed inset-0 z-50">
            <SchedulingModal
              open={isRescheduleModalOpen}
              onExit={handleRescheduleModalClose}
              listenerId={rescheduleData.listenerId}
              listenerAvailableNow={false}
              availabilities={rescheduleAvailabilities}
              listenerName={rescheduleData.listenerName}
              listenerImage={rescheduleData.listenerImage}
              originalRequestId={rescheduleData.originalRequestId}
              location="call_details_modal"
              initialTime={rescheduleData.initialTime}
              initialTagGroupId={rescheduleData.initialTagGroupId}
              initialText={rescheduleData.initialText}
            />
          </div>
        )}
        {isCancelModalOpen && callToCancel && (
          <CancelCallModal
            open={isCancelModalOpen}
            onClose={handleCancelModalClose}
            onConfirm={handleCancelCallConfirm}
            listenerName={callToCancel.listenerName}
            isCancelling={isCancelling}
            dataTestId="cancel-call-modal"
          />
        )}
        {!onOpenHowCallWorksFromParent && (
          <HowCallWorksModal open={isHowCallWorksModalOpen} onClose={() => setIsHowCallWorksModalOpen(false)} />
        )}
      </>
    );
  }

  // If there's only one active call and no scheduled calls or pending requests, show active call view directly
  if (activeCalls && activeCalls.length === 1 && scheduledCalls.length === 0 && pendingRequests.length === 0) {
    const activeCall = activeCalls[0];

    return (
      <>
        {open && (
          <DrawerModal open={open} onClose={onClose} header={renderHeader()} footer={renderFooter()}>
            <ActiveCallDetailsContent activeCall={activeCall} onOpenHowCallWorks={handleOpenHowCallWorks} />
          </DrawerModal>
        )}
        {!onOpenHowCallWorksFromParent && (
          <HowCallWorksModal open={isHowCallWorksModalOpen} onClose={() => setIsHowCallWorksModalOpen(false)} />
        )}
      </>
    );
  }

  // If there's only one pending request and no active calls or scheduled calls, show pending request view directly
  if (pendingRequests.length === 1 && (!activeCalls || activeCalls.length === 0) && scheduledCalls.length === 0) {
    return (
      <>
        {open && (
          <DrawerModal open={open} onClose={onClose} header={renderHeader()} footer={renderFooter()}>
            <PendingCallDetailsContent
              callRequest={pendingRequests[0]}
              onCancelCall={handleCancelCall}
              onOpenHowCallWorks={handleOpenHowCallWorks}
              isCancelling={isCancelling}
              isPassed={isRequestPassed(pendingRequests[0].id, pendingRequests[0].connections[0]?.status)}
            />
          </DrawerModal>
        )}
        {isCancelModalOpen && callToCancel && (
          <CancelCallModal
            open={isCancelModalOpen}
            onClose={handleCancelModalClose}
            onConfirm={handleCancelCallConfirm}
            listenerName={callToCancel.listenerName}
            isCancelling={isCancelling}
            dataTestId="cancel-call-modal"
          />
        )}
        {!onOpenHowCallWorksFromParent && (
          <HowCallWorksModal open={isHowCallWorksModalOpen} onClose={() => setIsHowCallWorksModalOpen(false)} />
        )}
      </>
    );
  }

  // If there's only one scheduled call and no active calls or pending requests, check if it's active and show appropriate view
  if (scheduledCalls.length === 1 && (!activeCalls || activeCalls.length === 0) && pendingRequests.length === 0) {
    const activeCall = getActiveCallForRequest(scheduledCalls[0]);
    const showActiveView = activeCall !== null;

    return (
      <>
        {open && (
          <DrawerModal open={open} onClose={onClose} header={renderHeader()} footer={renderFooter()}>
            {showActiveView && activeCall ? (
              <ActiveCallDetailsContent activeCall={activeCall} onOpenHowCallWorks={handleOpenHowCallWorks} />
            ) : (
              <SingleCallDetailsContent
                callRequest={scheduledCalls[0]}
                onAddToCalendar={handleAddToCalendar}
                onCancelCall={handleCancelCall}
                onRescheduleCall={() => handleRescheduleCall(scheduledCalls[0])}
                onOpenHowCallWorks={handleOpenHowCallWorks}
                isCancelling={isCancelling}
                isDownloadingCalendar={isDownloadingCalendar}
              />
            )}
          </DrawerModal>
        )}
        {isRescheduleModalOpen && rescheduleData && (
          <div className="fixed inset-0 z-50">
            <SchedulingModal
              open={isRescheduleModalOpen}
              onExit={handleRescheduleModalClose}
              listenerId={rescheduleData.listenerId}
              listenerAvailableNow={false}
              availabilities={rescheduleAvailabilities}
              listenerName={rescheduleData.listenerName}
              listenerImage={rescheduleData.listenerImage}
              originalRequestId={rescheduleData.originalRequestId}
              location="call_details_modal"
              initialTime={rescheduleData.initialTime}
              initialTagGroupId={rescheduleData.initialTagGroupId}
              initialText={rescheduleData.initialText}
            />
          </div>
        )}
        {isCancelModalOpen && callToCancel && (
          <CancelCallModal
            open={isCancelModalOpen}
            onClose={handleCancelModalClose}
            onConfirm={handleCancelCallConfirm}
            listenerName={callToCancel.listenerName}
            isCancelling={isCancelling}
            dataTestId="cancel-call-modal"
          />
        )}
        {!onOpenHowCallWorksFromParent && (
          <HowCallWorksModal open={isHowCallWorksModalOpen} onClose={() => setIsHowCallWorksModalOpen(false)} />
        )}
      </>
    );
  }

  // Show list view (multiple calls or active calls present)
  return (
    <>
      {open && (
        <DrawerModal open={open} onClose={onClose} header={renderHeader()} footer={renderFooter()}>
          <CallListContent
            callRequests={scheduledCalls}
            pendingRequests={pendingRequests}
            activeCalls={activeCalls || []}
            onSelectCall={(callRequest) => {
              setSelectedCall(callRequest);
              setSelectedActiveCall(null);
            }}
            onSelectActiveCall={(activeCall) => {
              setSelectedActiveCall(activeCall);
              setSelectedCall(null);
            }}
          />
        </DrawerModal>
      )}
      {!onOpenHowCallWorksFromParent && (
        <HowCallWorksModal open={isHowCallWorksModalOpen} onClose={() => setIsHowCallWorksModalOpen(false)} />
      )}
    </>
  );
};
